import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import './App.css';
import McpPlayground from './McpPlayground';
import ApiPanel from './ApiPanel';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const INSTRUCTOR = 'instructor';
const SHOW_PROFILE_DEBUG = process.env.REACT_APP_SHOW_PROFILE_DEBUG !== 'false';
const DEFAULT_COURSE_SLUG = 's26-riosalado-21544-ppp';
const COURSE_SLUG_COOKIE = 'la_course_slug';
const COURSE_SLUG_COOKIE_MAX_AGE_SEC = 60 * 60 * 24 * 365; // 1 year

axios.defaults.withCredentials = true;

function readCookie(name) {
  const prefix = `${encodeURIComponent(name)}=`;
  const parts = document.cookie.split(';');
  for (const part of parts) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      try {
        return decodeURIComponent(trimmed.slice(prefix.length));
      } catch (_) {
        return trimmed.slice(prefix.length);
      }
    }
  }
  return null;
}

function writeCookie(name, value, maxAgeSec) {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie =
    `${encodeURIComponent(name)}=${encodeURIComponent(value)}` +
    `; Path=/; Max-Age=${maxAgeSec}; SameSite=Lax${secure}`;
}

function initialCourseSlug() {
  const saved = (readCookie(COURSE_SLUG_COOKIE) || '').trim();
  return saved || DEFAULT_COURSE_SLUG;
}

function App() {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [courseSlug, setCourseSlug] = useState(initialCourseSlug);
  const [courseDraft, setCourseDraft] = useState(initialCourseSlug);
  const [courseCatalog, setCourseCatalog] = useState([]);
  const [courseMenuOpen, setCourseMenuOpen] = useState(false);
  const [dataMode, setDataMode] = useState('anon');
  const [actingAs, setActingAs] = useState(INSTRUCTOR);
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentSnapshot, setStudentSnapshot] = useState(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [debugLog, setDebugLog] = useState(null);
  const [mcpOpen, setMcpOpen] = useState(false);
  const [apiOpen, setApiOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const courseComboRef = useRef(null);

  const sidePanelOpen = Boolean(debugLog) || mcpOpen || apiOpen;
  const isInstructor = actingAs === INSTRUCTOR;
  const canUseReal = Boolean(
    user &&
      (user.role === 'admin' ||
        (user.role === 'real' && (user.real_courses || []).includes(courseSlug)))
  );

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/me`)
      .then((res) => setUser(res.data))
      .catch(() => setUser(null))
      .finally(() => setAuthChecked(true));
  }, []);

  useEffect(() => {
    if (!user) {
      setCourseCatalog([]);
      return;
    }
    axios
      .get(`${API_BASE_URL}/courses`)
      .then((res) => setCourseCatalog(res.data.courses || []))
      .catch((err) => {
        console.error('Failed to load course catalog:', err);
        setCourseCatalog([]);
      });
  }, [user]);

  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!courseComboRef.current?.contains(e.target)) {
        setCourseMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  useEffect(() => {
    if (!canUseReal && dataMode === 'real') {
      setDataMode('anon');
    }
  }, [canUseReal, dataMode]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (!isLoading && user) {
      inputRef.current?.focus();
    }
  }, [isLoading, user]);

  const loadStudents = useCallback(async () => {
    if (!user) return;
    setStudentsLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/students/${encodeURIComponent(courseSlug)}`,
        { params: { data_mode: dataMode } }
      );
      const rows = response.data.students || [];
      setStudents(rows);
    } catch (error) {
      console.error('Failed to load students:', error);
      setStudents([]);
    } finally {
      setStudentsLoading(false);
    }
  }, [courseSlug, dataMode, user]);

  const loadSnapshot = useCallback(
    async (studentId) => {
      if (!user || !studentId || studentId === INSTRUCTOR) {
        setStudentSnapshot(null);
        return;
      }
      setSnapshotLoading(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/student-snapshot`, {
          params: {
            course_slug: courseSlug,
            student_id: studentId,
            data_mode: dataMode,
          },
        });
        setStudentSnapshot(response.data);
      } catch (error) {
        console.error('Failed to load student snapshot:', error);
        setStudentSnapshot({ error: error.message });
      } finally {
        setSnapshotLoading(false);
      }
    },
    [courseSlug, dataMode, user]
  );

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  useEffect(() => {
    loadSnapshot(actingAs);
  }, [actingAs, loadSnapshot]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/login`, {
        username: loginUsername.trim(),
        password: loginPassword,
      });
      setUser(res.data.user);
      setLoginPassword('');
    } catch (err) {
      setLoginError(err.response?.data?.detail || err.message || 'Login failed');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API_BASE_URL}/logout`);
    } catch (_) {
      /* ignore */
    }
    setUser(null);
    setMessages([]);
    setDebugLog(null);
    setMcpOpen(false);
  };

  const handlePersonaChange = (value) => {
    setActingAs(value);
    setMessages([]);
    setDebugLog(null);
  };

  const handleCourseChange = (value) => {
    const next = (value || '').trim();
    setCourseDraft(next);
    if (!next || next === courseSlug) return;
    setCourseSlug(next);
    writeCookie(COURSE_SLUG_COOKIE, next, COURSE_SLUG_COOKIE_MAX_AGE_SEC);
    setActingAs(INSTRUCTOR);
    setMessages([]);
    setDebugLog(null);
    setStudentSnapshot(null);
  };

  const filteredCourses = (() => {
    const q = courseDraft.trim().toLowerCase();
    if (!q) return courseCatalog;
    return courseCatalog.filter((c) => {
      const slug = (c.course_slug || '').toLowerCase();
      const name = (c.name || '').toLowerCase();
      return slug.includes(q) || name.includes(q);
    });
  })();

  const commitCourseDraft = () => {
    handleCourseChange(courseDraft);
    setCourseMenuOpen(false);
  };

  const selectCourse = (slug) => {
    setCourseDraft(slug);
    handleCourseChange(slug);
    setCourseMenuOpen(false);
  };

  const handleDataModeChange = (value) => {
    setDataMode(value);
    setActingAs(INSTRUCTOR);
    setMessages([]);
    setDebugLog(null);
    setStudentSnapshot(null);
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const question = inputValue.trim();
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: question,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const history = messages
        .filter((m) => m.type === 'user' || m.type === 'assistant')
        .map((m) => ({
          role: m.type === 'user' ? 'user' : 'assistant',
          content: m.content,
        }))
        .slice(-20);

      const response = await axios.post(`${API_BASE_URL}/query-agent`, {
        course_slug: courseSlug,
        question,
        history,
        acting_as: actingAs,
        data_mode: dataMode,
      });

      const assistantMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: response.data.answer,
        model: response.data.model,
        provider: response.data.provider,
        debugLog: response.data.debug_log,
        dataMode: response.data.data_mode,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        type: 'error',
        content: `Error: ${error.response?.data?.detail || error.message}`,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Example questions that the current MCP tools answer well, per persona. They name
  // what this course actually has: a real student, and the student's weakest topic.
  const suggestedQuestions = () => {
    if (isInstructor) {
      const sample = students[0]?.person_name;
      return [
        'What metrics does this course track?',
        'Who is strongest in each mastery topic?',
        'Who has the highest procrastination index?',
        sample ? `How is ${sample} doing?` : 'How many students are enrolled in the course?',
        'Which students have the lowest mastery, and in which topics?',
      ];
    }
    const topics = (studentSnapshot?.metrics_by_topic || []).filter(
      (t) => t.metric === 'mastery' && t.topic
    );
    const weakest = topics.length
      ? topics.reduce((a, b) => (b.value < a.value ? b : a)).topic
      : null;
    return [
      'Which topics am I strongest and weakest in?',
      weakest ? `How can I get better at ${weakest}?` : 'What should I focus on next?',
      'How sure are you about those estimates?',
      'Am I keeping up with assignment deadlines?',
    ];
  };

  const renderSuggestions = () => (
    <ul className="suggestions">
      {suggestedQuestions().map((q) => (
        <li key={q}>
          <button
            type="button"
            className="suggestion"
            onClick={() => {
              setInputValue(q);
              inputRef.current?.focus();
            }}
          >
            {q}
          </button>
        </li>
      ))}
    </ul>
  );

  const clearChat = () => {
    setMessages([]);
    setDebugLog(null);
  };

  const renderSnapshotPanel = () => {
    if (isInstructor) return null;

    if (snapshotLoading) {
      return <div className="snapshot-panel loading">Loading student profile…</div>;
    }

    if (!studentSnapshot || studentSnapshot.error) {
      return (
        <div className="snapshot-panel error">
          Could not load profile for {actingAs}.
        </div>
      );
    }

    const metrics = studentSnapshot.metrics || {};
    const summaries = studentSnapshot.metric_summaries || {};
    const posts = studentSnapshot.recent_posts || [];
    // Per-topic beliefs that have a name: mastery per topic cluster, or one overall
    // "Assessment performance". Rows keyed by bare LO ids (practice) mean nothing to a
    // reader, so they stay out of the panel.
    const topics = (studentSnapshot.metrics_by_topic || []).filter((t) => t.topic);

    return (
      <div className="snapshot-panel">
        <h3>Your learning profile</h3>
        {SHOW_PROFILE_DEBUG && (
          <p className="snapshot-debug-subtitle">
            Debug (instructor demo only): {actingAs} · {courseSlug} · {dataMode} ·{' '}
            {Object.keys(metrics).length} metrics · {topics.length} topics · {posts.length} evidence posts
          </p>
        )}
        <div className="snapshot-metrics">
          {Object.entries(metrics).map(([name, value]) => (
            <div key={name} className="snapshot-metric">
              <strong>{name.replace(/_/g, ' ')}</strong>
              <span>{value}</span>
              {summaries[name] && <small>{summaries[name]}</small>}
            </div>
          ))}
          {topics.map((t) => {
            const key = `${t.metric}@${t.lo_id}`;
            const label = t.metric === 'mastery' ? t.topic : `${t.metric.replace(/_/g, ' ')}: ${t.topic}`;
            return (
              <div key={key} className="snapshot-metric" title={t.topic_description || ''}>
                <strong>{label}</strong>
                <span>{t.value}</span>
                {summaries[key] && <small>{summaries[key]}</small>}
              </div>
            );
          })}
        </div>
        {posts.length > 0 && (
          <details className="snapshot-posts">
            <summary>Recent discussion evidence ({posts.length})</summary>
            {posts.map((post) => (
              <div key={post.assignment_id || post.ts} className="snapshot-post">
                {(post.supports_metrics || []).map((support) => (
                  <div
                    key={`${post.assignment_id}-${support.metric}`}
                    className="snapshot-support"
                  >
                    <em>{support.metric}</em>: {support.text}
                    {support.rationale && (
                      <span className="snapshot-rationale"> — {support.rationale}</span>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </details>
        )}
      </div>
    );
  };

  if (!authChecked) {
    return (
      <div className="app login-app">
        <div className="login-card">
          <p>Checking session…</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="app login-app">
        <form className="login-card" onSubmit={handleLogin}>
          <h1>Learning Analytics</h1>
          <p className="login-sub">Sign in to query course analytics</p>
          <label htmlFor="login-user">Username</label>
          <input
            id="login-user"
            value={loginUsername}
            onChange={(e) => setLoginUsername(e.target.value)}
            autoComplete="username"
            required
          />
          <label htmlFor="login-pass">Password</label>
          <input
            id="login-pass"
            type="password"
            value={loginPassword}
            onChange={(e) => setLoginPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
          {loginError && <div className="login-error">{loginError}</div>}
          <button type="submit" disabled={loginLoading}>
            {loginLoading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="app">
      <div className="header">
        <h1>Learning Analytics Assistant</h1>
        <div className="header-controls">
          <div className="user-badge">
            <span>
              {user.username} · {user.role}
            </span>
            <button type="button" className="logout-button" onClick={handleLogout}>
              Log out
            </button>
          </div>
          <div className="course-input" ref={courseComboRef}>
            <label htmlFor="course-slug">Course:</label>
            <div className="course-combobox">
              <input
                id="course-slug"
                type="text"
                role="combobox"
                aria-expanded={courseMenuOpen}
                aria-controls="course-slug-listbox"
                aria-autocomplete="list"
                value={courseDraft}
                onChange={(e) => {
                  setCourseDraft(e.target.value);
                  setCourseMenuOpen(true);
                }}
                onFocus={() => setCourseMenuOpen(true)}
                onBlur={commitCourseDraft}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitCourseDraft();
                  } else if (e.key === 'Escape') {
                    setCourseDraft(courseSlug);
                    setCourseMenuOpen(false);
                  } else if (e.key === 'ArrowDown') {
                    setCourseMenuOpen(true);
                  }
                }}
                placeholder="Select or type a course slug"
                autoComplete="off"
              />
              <button
                type="button"
                className="course-combobox-toggle"
                aria-label="Show courses"
                tabIndex={-1}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setCourseMenuOpen((open) => !open)}
              >
                ▾
              </button>
              {courseMenuOpen && (
                <ul
                  id="course-slug-listbox"
                  className="course-combobox-menu"
                  role="listbox"
                >
                  {filteredCourses.length === 0 ? (
                    <li className="course-combobox-empty" role="presentation">
                      {courseCatalog.length === 0
                        ? 'No courses from course_index — type a slug'
                        : 'No matches — press Enter to use this slug'}
                    </li>
                  ) : (
                    filteredCourses.map((c) => (
                      <li key={c.course_slug} role="option">
                        <button
                          type="button"
                          className={
                            c.course_slug === courseSlug
                              ? 'course-option selected'
                              : 'course-option'
                          }
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => selectCourse(c.course_slug)}
                        >
                          <span className="course-option-slug">{c.course_slug}</span>
                          {c.name && c.name !== c.course_slug && (
                            <span className="course-option-name">{c.name}</span>
                          )}
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              )}
            </div>
          </div>
          {canUseReal && (
            <div className="persona-input">
              <label htmlFor="data-mode">Data:</label>
              <select
                id="data-mode"
                value={dataMode}
                onChange={(e) => handleDataModeChange(e.target.value)}
              >
                <option value="anon">Anonymized</option>
                <option value="real">Real names</option>
              </select>
            </div>
          )}
          {!canUseReal && (
            <div className="persona-input">
              <span className="data-mode-fixed">Data: anonymized</span>
            </div>
          )}
          <div className="persona-input">
            <label htmlFor="acting-as">Speaking as:</label>
            <select
              id="acting-as"
              value={actingAs}
              onChange={(e) => handlePersonaChange(e.target.value)}
              disabled={studentsLoading}
            >
              <option value={INSTRUCTOR}>Instructor</option>
              {students.map((student) => (
                <option key={student.person_id} value={student.person_name}>
                  {student.person_name}
                </option>
              ))}
            </select>
          </div>
          <button onClick={clearChat} className="clear-button">
            Clear Chat
          </button>
          {user.role === 'admin' && (
            <button
              type="button"
              className={`mcp-toggle-button ${mcpOpen ? 'active' : ''}`}
              onClick={() => {
                setMcpOpen((open) => {
                  if (!open) {
                    setDebugLog(null);
                    setApiOpen(false);
                  }
                  return !open;
                });
              }}
            >
              {mcpOpen ? 'Hide MCP' : 'MCP'}
            </button>
          )}
          <button
            type="button"
            className={`mcp-toggle-button ${apiOpen ? 'active' : ''}`}
            onClick={() => {
              setApiOpen((open) => {
                if (!open) {
                  setDebugLog(null);
                  setMcpOpen(false);
                }
                return !open;
              });
            }}
          >
            {apiOpen ? 'Hide API' : 'API'}
          </button>
        </div>
      </div>

      <div
        className={`chat-container ${sidePanelOpen ? 'with-debug' : ''} ${
          !isInstructor ? 'with-snapshot' : ''
        }`}
      >
        {!isInstructor && renderSnapshotPanel()}

        <div className="chat-main">
          <div className="messages">
            {messages.length === 0 && (
              <div className="welcome-message">
                {isInstructor ? (
                  <>
                    <h3>Welcome, Instructor!</h3>
                    <p>
                      Viewing <strong>{dataMode === 'real' ? 'real' : 'anonymized'}</strong>{' '}
                      data for this course. Ask questions about your course data. For example
                      (click one to use it):
                    </p>
                    {renderSuggestions()}
                  </>
                ) : (
                  <>
                    <h3>Hi! I&apos;m your learning coach.</h3>
                    <p>
                      I&apos;m here to help you based on <strong>your</strong> progress in this
                      course. Your profile is loaded on the left — ask me anything about how
                      you&apos;re doing or what to focus on next.
                    </p>
                    {renderSuggestions()}
                  </>
                )}
              </div>
            )}

            {messages.map((message) => (
              <div key={message.id} className={`message ${message.type}`}>
                <div className="message-header">
                  <span className="message-type">
                    {message.type === 'user'
                      ? 'You'
                      : message.type === 'assistant'
                        ? isInstructor
                          ? 'Assistant'
                          : 'Coach'
                        : 'Error'}
                  </span>
                  <span className="message-time">
                    {message.timestamp.toLocaleTimeString()}
                  </span>
                </div>
                <div className="message-content">{message.content}</div>
                {message.type === 'assistant' && message.model && (
                  <div className="message-meta">
                    Model: {message.model} ({message.provider})
                    {message.dataMode ? ` · ${message.dataMode}` : ''}
                  </div>
                )}
                {message.debugLog && (
                  <button
                    className="debug-button"
                    onClick={() => {
                      if (debugLog === message.debugLog) {
                        setDebugLog(null);
                      } else {
                        setMcpOpen(false);
                        setApiOpen(false);
                        setDebugLog(message.debugLog);
                      }
                    }}
                  >
                    {debugLog === message.debugLog ? 'Hide Debug' : 'Debug'}
                  </button>
                )}
              </div>
            ))}

            {isLoading && (
              <div className="message assistant">
                <div className="message-content">
                  <div className="loading">Thinking...</div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <div className="input-container">
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={
                isInstructor
                  ? 'Ask a question about your course data...'
                  : 'Ask your coach about your progress...'
              }
              disabled={isLoading}
              rows={2}
            />
            <button
              onClick={sendMessage}
              disabled={!inputValue.trim() || isLoading}
              className="send-button"
            >
              {isLoading ? 'Sending...' : 'Send'}
            </button>
          </div>
        </div>
      </div>
      {debugLog && (
        <div className="debug-panel">
          <div className="debug-panel-header">
            <span>Debug Log ({debugLog.length} steps)</span>
            <button onClick={() => setDebugLog(null)}>Close</button>
          </div>
          <div className="debug-panel-content">
            {debugLog.map((entry, i) => (
              <div key={i} className={`debug-step debug-${entry.type}`}>
                <div className="debug-step-header">
                  Step {entry.step}: {entry.type}
                  {entry.tool && <span className="debug-tool-name"> - {entry.tool}</span>}
                </div>
                {entry.args && (
                  <pre className="debug-step-args">{JSON.stringify(entry.args, null, 2)}</pre>
                )}
                <pre className="debug-step-content">{entry.content}</pre>
              </div>
            ))}
          </div>
        </div>
      )}
      {apiOpen && (
        <ApiPanel
          apiBaseUrl={API_BASE_URL}
          courseSlug={courseSlug}
          dataMode={dataMode}
          studentId={isInstructor ? students[0]?.person_name : actingAs}
          username={user.username}
          onClose={() => setApiOpen(false)}
        />
      )}
      {mcpOpen && user.role === 'admin' && (
        <McpPlayground
          apiBaseUrl={API_BASE_URL}
          courseSlug={courseSlug}
          onClose={() => setMcpOpen(false)}
        />
      )}
    </div>
  );
}

export default App;
